import React from "react";
import moment, { Moment } from "moment";
import { intl } from "LocaleWrapper";

export type DateProperty = Moment | Date | number | string | null | undefined;
type FormatProperty = string;

interface FormattedDateProps {
	date?: DateProperty;
	format?: FormatProperty;
	withoutYear?: boolean;
}

interface FormattedDateWindowProps {
	from?: DateProperty;
	to?: DateProperty;
	format?: FormatProperty;
	withoutYear?: boolean;
}

export const formatDate = (date: DateProperty, format = "lll", withoutYear = false) => {
	if (date && moment(date).isValid()) {
		const year = moment(date).get("year").toString();
		return moment(date).format(format).replace(withoutYear ? year : "", "");
	}
	return null;
};

export const FormattedDate = ({ date, format = "lll", withoutYear = false }: FormattedDateProps) => {
	const formattedDate = formatDate(date, format, withoutYear);
	if (formattedDate == null) {
		return <React.Fragment />;
	}
	return formattedDate;
};

export const FormattedDateWindow = ({ from, to, format = "lll", withoutYear = false }: FormattedDateWindowProps) => {

	const validFrom = from && moment(from).isValid();
	const validTo = to && moment(to).isValid();
	const isSameDay = moment(from).isSame(to, "day");
	const endFormat = format === "lll" && isSameDay ? "HH:mm" : format;

	if (validFrom || validTo) {
		return (
			<React.Fragment>
				{validFrom ? (
					<FormattedDate date={from} format={format} withoutYear={withoutYear} />
				) : (
					intl.formatMessage({ id: "UNKNOWN" })
				)}

				{" - "}

				{validTo ? (
					<FormattedDate date={to} format={endFormat} withoutYear={withoutYear} />
				) : (
					intl.formatMessage({ id: "UNKNOWN" })
				)}
			</React.Fragment>
		);
	} else {
		return intl.formatMessage({ id: "UNKNOWN" });
	}
};

export const FormattedCalendarDate = ({ date }: { date: DateProperty }) => {
	return (date && moment(date).isValid()) ? moment(date).calendar() : <React.Fragment />;
};

export const FormattedTime = ({ minutes }: { minutes?: number }) => {
	return minutes ? moment().startOf("day").add(minutes, "minutes").format("HH:mm") : intl.formatMessage({ id: "UNKNOWN" });
};

export const FormattedMinutes = ({ minutes }: { minutes?: number }) => {
	if (minutes == null) {
		return intl.formatMessage({ id: "UNKNOWN" });
	}

	const roundedTotal = Math.round(minutes);

	const hours = Math.floor(roundedTotal / 60);
	const mins = roundedTotal % 60;

	return `${hours.toString().padStart(2, "0")}:${mins
		.toString()
		.padStart(2, "0")}
		${intl.formatMessage({ id: "HOURS" })}`;
};

// eslint-disable-next-line react-refresh/only-export-components
export const DateUtils = {

	getTimestamp: (date?: DateProperty) => {
		return (date && moment(date).isValid()) ? moment(date).unix() * 1000 : null;
	},

	convertMinutesToTimeUnits: (input: number) => {

		const days = input / 60 / 24;
		const hours = (days % 1) * 24;
		const minutes = (hours % 1) * 60;

		return {
			days: Math.floor(days),
			hours: Math.floor(hours),
			minutes: Math.round(minutes),
		};
	},

	getDatePickerLocale: (language: string | null | undefined) => {
		return language ? (language.toLowerCase().startsWith("en") ? "en_GB" : language.toLowerCase().slice(0, 2)) : "en_GB";
	},

	getDateFormat: (language: string) => {
		if (language.toLowerCase().startsWith("en")) {
			return "dd/MM/yyyy";
		} else if (language.toLowerCase().startsWith("de")) {
			return "dd.MM.yyyy";
		} else if (language.toLowerCase().startsWith("vi")) {
			return "dd/MM/yyyy";
		} else {
			return "dd-MM-yyyy";
		}
	},
	getTimeFormat: () => {
		return "HH:mm";
	},

	getDateTimeFormat: (language: string) => {
		return `${DateUtils.getDateFormat(language)} ${DateUtils.getTimeFormat()}`;
	},

	getDateOrUndefined: (date: DateProperty) => date ? moment(date).toDate() : undefined,
};
